import { isDefined } from "@omni/utils/helper.utils";
import queryString from "query-string";
import {
    createContext,
    PropsWithChildren,
    use,
    useEffect,
    useMemo,
    useState
} from "react";

import * as CalculatorStepTracker from "@/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@/services/tracking/trackers/tools/calculators/config";

import { GravelShoppingList } from "./gravelCalculator.types";

type GravelCalculationContextValue = {
    setLength: (length: string) => void;
    setWidth: (width: string) => void;
    setThickness: (thickness: string) => void;
    setWeight: (thickness: string) => void;
    length: string;
    width: string;
    thickness: string;
    weight: string;
    result: GravelShoppingList;
    hasValidResult: boolean;
};

type CallToActionContextProviderProps = PropsWithChildren<{}>;

export const GravelCalculationContext = createContext<
    GravelCalculationContextValue | undefined
>(undefined);
GravelCalculationContext.displayName = "GravelCalculationContext";

export const GravelCalculationContextProvider = ({
    children
}: CallToActionContextProviderProps) => {
    const [length, setLength] = useState<string>("");
    const [width, setWidth] = useState<string>("");
    const [thickness, setThickness] = useState<string>("");
    const [weight, setWeight] = useState<string>("16");

    useEffect(() => {
        const q = queryString.parse(location.search, {
            parseNumbers: true,
            parseBooleans: true
        });

        setLength(q.gLength?.toString() || "");
        setWidth(q.width?.toString() || "");
        setThickness(q.thickness?.toString() || "");
        setWeight(q.weight?.toString() || "16");
    }, []);

    const result = useMemo<GravelShoppingList>(() => {
        const thicknessNr = Number(thickness);
        const widthNr = Number(width);
        const lengthNr = Number(length);
        const weightNr = Number(weight);

        const volume = thicknessNr * widthNr * lengthNr;
        const kgGravel = volume * 1500;

        return {
            volume,
            kgGravel,
            bags: {
                amount: Math.ceil(kgGravel / weightNr),
                weight: weightNr
            }
        };
    }, [length, width, thickness, weight]);

    const hasValidResult = useMemo(() => {
        return result.volume > 0;
    }, [result]);

    useEffect(() => {
        CalculatorStepTracker.trackStart(CALCULATOR_KEYS.gravel);
    }, []);

    return (
        <GravelCalculationContext
            value={{
                setLength,
                setWidth,
                setThickness,
                setWeight,
                length,
                width,
                thickness,
                weight,
                result,
                hasValidResult
            }}
        >
            {children}
        </GravelCalculationContext>
    );
};

export const useGravelCalculationContext = () => {
    const context = use<GravelCalculationContextValue | undefined>(
        GravelCalculationContext
    );

    if (!isDefined(context)) {
        throw new Error(
            `${GravelCalculationContext.displayName} is undefined.`
        );
    }

    return context;
};
